/**
 * Komprimerings- og kompleksitetsvisning (Fase 2C, task #277)
 *
 * Viser brukeren transparent:
 * - Hva som ble komprimert (rå antall → unike, hvilke ble slått sammen)
 * - Kompleksitetsvurdering (Bloom-poeng vs. kapasitet)
 * - Varsel ved too_complex / too_simple
 *
 * Ingen override-knapp i første runde — vi måler først om over-aggressiv
 * komprimering er reelt problem før vi bygger det.
 */

import { useState } from 'react';
import { ChevronDown, ChevronUp, GitMerge, Activity, AlertTriangle, CheckCircle2, Info } from 'lucide-react';

interface UniqueOutcome {
  text: string;
  bloom_level: string;
  source_descriptors?: string[];
  fagord?: string[];
}

interface CompressionData {
  raw_count: number;
  unique_count: number;
  merged_count: number;
  reduction_ratio: number;
  unique: UniqueOutcome[];
  compression_summary: string;
}

interface ComplexityCheck {
  status: 'balanced' | 'too_complex' | 'too_simple' | 'no_outcomes';
  severity?: 'success' | 'warning' | 'info';
  ratio?: number;
  bloom_points?: number;
  capacity?: number;
  prior_knowledge?: string;
  prior_knowledge_factor?: number;
  hours?: number;
  total_hours_estimate?: number;
  self_study_multiplier?: number;
  outcome_count?: number;
  bloom_distribution?: Record<string, number>;
  message?: string;
  recommendation?: string | null;
  recommended_outcome_count?: number;
}

interface CompressionDisplayProps {
  compression: CompressionData | null;
  complexityCheck: ComplexityCheck | null;
}

const PRIOR_KNOWLEDGE_LABELS: Record<string, string> = {
  ingen:       'ingen forkunnskaper',
  lav:         'lav (grunnskole/videregående)',
  middels:     'middels (fagbrev/relevant praksis)',
  hoy:         'høy (bachelor/høgskole)',
  svaert_hoy:  'svært høy (master/spesialist)',
  ukjent:      'ukjent'
};

const BLOOM_LEVEL_LABELS: Record<string, string> = {
  huske:       'Huske',
  forstå:      'Forstå',
  forstaa:     'Forstå',
  anvende:     'Anvende',
  analysere:   'Analysere',
  vurdere:     'Vurdere',
  evaluere:    'Vurdere',
  skape:       'Skape',
  syntetisere: 'Skape'
};

function ComplexityStatusIcon({ status, severity }: { status: ComplexityCheck['status']; severity?: ComplexityCheck['severity'] }) {
  if (status === 'balanced' || severity === 'success') {
    return <CheckCircle2 className="w-5 h-5 text-green-600" />;
  }
  if (status === 'too_complex') {
    return <AlertTriangle className="w-5 h-5 text-kf-orange-500" />;
  }
  if (status === 'too_simple') {
    return <Info className="w-5 h-5 text-kf-blue-500" />;
  }
  return <Activity className="w-5 h-5 text-gray-500" />;
}

export default function CompressionDisplay({ compression, complexityCheck }: CompressionDisplayProps) {
  const [showDetails, setShowDetails] = useState(false);

  if (!compression && !complexityCheck) return null;

  const mergedUnique = (compression?.unique || []).filter(u => (u.source_descriptors || []).length > 1);

  return (
    <div className="space-y-3">
      {/* Komprimerings-boks — vises kun når det er faktisk kvalitetssjekk-resultat
          (task #306, 2026-07-02). Ved Bloom-classifier-fallback får vi et
          compression-objekt uten compression_summary — da er det ingenting
          meningsfullt å vise, og boksen skjules helt. */}
      {compression && compression.compression_summary && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <GitMerge className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-purple-900 mb-1">Kvalitetssjekk av læringsutbytter</h4>
              <p className="text-sm text-purple-800">{compression.compression_summary}</p>

              {compression.merged_count > 0 && (
                <button
                  type="button"
                  onClick={() => setShowDetails(v => !v)}
                  className="mt-2 text-xs text-purple-700 hover:text-purple-900 hover:underline flex items-center gap-1"
                >
                  {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  {showDetails ? 'Skjul detaljer' : `Vis ${compression.merged_count} sammenslåtte grupper`}
                </button>
              )}

              {showDetails && (
                <div className="mt-3 space-y-3 border-t border-purple-200 pt-3">
                  {mergedUnique.map((u, idx) => (
                    <div key={idx} className="bg-white rounded p-3 border border-purple-100">
                      <div className="text-sm text-gray-900 font-medium mb-1">{u.text}</div>
                      <div className="text-xs text-gray-500">
                        Bloom-nivå: <span className="font-medium text-gray-700">{BLOOM_LEVEL_LABELS[u.bloom_level] || u.bloom_level}</span>
                        {(u.fagord || []).length > 0 && (
                          <> · Fagord: <span className="text-gray-700">{(u.fagord || []).join(', ')}</span></>
                        )}
                      </div>
                      <div className="text-xs text-purple-600 mt-1">
                        ← slått sammen fra: {(u.source_descriptors || []).join(' + ')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Kompleksitets-boks */}
      {complexityCheck && complexityCheck.status !== 'no_outcomes' && (
        <div className={`border rounded-lg p-4 ${
          complexityCheck.status === 'balanced' ? 'bg-green-50 border-green-200' :
          complexityCheck.status === 'too_complex' ? 'bg-kf-orange-50 border-kf-orange-200' :
          'bg-kf-blue-50 border-kf-blue-200'
        }`}>
          <div className="flex items-start gap-3">
            <ComplexityStatusIcon status={complexityCheck.status} severity={complexityCheck.severity} />
            <div className="flex-1 min-w-0">
              <h4 className={`font-semibold mb-1 ${
                complexityCheck.status === 'balanced' ? 'text-green-900' :
                complexityCheck.status === 'too_complex' ? 'text-kf-orange-900' :
                'text-kf-blue-900'
              }`}>
                Kompleksitetsvurdering: {
                  complexityCheck.status === 'balanced' ? 'Balansert' :
                  complexityCheck.status === 'too_complex' ? 'For komplekst' :
                  complexityCheck.status === 'too_simple' ? 'Underutnyttet' : '—'
                }
              </h4>

              <div className="text-sm space-y-1">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <div>
                    <span className="text-gray-600">Bloom-poeng:</span>{' '}
                    <span className="font-medium">{complexityCheck.bloom_points} pts</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Kapasitet:</span>{' '}
                    <span className="font-medium">{complexityCheck.capacity} pts</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Timer (klasse):</span>{' '}
                    <span className="font-medium">{complexityCheck.hours}t</span>
                    {complexityCheck.total_hours_estimate && (
                      <span className="text-gray-500"> (~{complexityCheck.total_hours_estimate}t inkl. selvstudier)</span>
                    )}
                  </div>
                  <div>
                    <span className="text-gray-600">Forkunnskap:</span>{' '}
                    <span className="font-medium">{PRIOR_KNOWLEDGE_LABELS[complexityCheck.prior_knowledge || 'ukjent']}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Antall læringsutbytter:</span>{' '}
                    <span className="font-medium">{complexityCheck.outcome_count}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Ratio:</span>{' '}
                    <span className="font-medium">{complexityCheck.ratio}</span>
                  </div>
                </div>

                {complexityCheck.bloom_distribution && Object.keys(complexityCheck.bloom_distribution).length > 0 && (
                  <div className="text-xs text-gray-600 mt-2">
                    Bloom-fordeling: {Object.entries(complexityCheck.bloom_distribution).map(([level, count]) => (
                      <span key={level} className="inline-block mr-2">
                        {BLOOM_LEVEL_LABELS[level] || level}: <span className="font-medium">{count}</span>
                      </span>
                    ))}
                  </div>
                )}

                {complexityCheck.status !== 'balanced' && complexityCheck.message && (
                  <p className={`mt-2 text-xs ${
                    complexityCheck.status === 'too_complex' ? 'text-kf-orange-800' : 'text-kf-blue-800'
                  }`}>
                    {complexityCheck.message}
                  </p>
                )}

                {complexityCheck.recommendation && (
                  <p className={`mt-1 text-xs font-medium ${
                    complexityCheck.status === 'too_complex' ? 'text-kf-orange-900' : 'text-kf-blue-900'
                  }`}>
                    {complexityCheck.recommendation}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
